/**
 * Vocabulário dos treinamentos — as frases que a tela diz, num lugar só.
 *
 * Fora daqui não se escreve texto de treinamento. Dois motivos concretos:
 *
 *  - **a frase de "o vídeo ainda não subiu" aparece em três telas** (lista, curso e aula)
 *    e precisa dizer a MESMA coisa nas três. Rótulo de fase anterior sobrando numa delas
 *    é exatamente o erro que este projeto já pagou: a tela afirma sem medir e ninguém
 *    reclama porque ela parece certa;
 *  - o teste consegue afirmar o vocabulário sem renderizar a árvore inteira.
 *
 * Nada aqui decide regra de negócio nem calcula métrica (invariante 3): o "3 de 12" vem
 * pronto do servidor, e estas funções só o escrevem em português.
 */

/** A frase que ensina o termo, no cabeçalho da lista. */
export const O_QUE_E =
  'Cursos curtos sobre operar grupos de WhatsApp com tráfego pago. O progresso é seu, ' +
  'não do workspace: você continua de onde parou mesmo trocando de conta.';

/**
 * O estado em que este produto nasce, dito em palavras.
 *
 * Aula sem vídeo **não é defeito e não é aula faltando**: o conteúdo escrito já está lá e
 * o vídeo entra depois (decisão do dono em 18/09). A tela mostra a aula, explica a
 * ausência e deixa concluir — sumir com ela transformaria "ainda não gravamos" em "não
 * existe", que é ausência de resposta valendo como resposta (invariante 12).
 */
export const SEM_VIDEO = {
  /** Selo ao lado do título da aula. */
  badge: 'Sem vídeo ainda',
  /** Título do bloco que ocupa o lugar do player. */
  title: 'O vídeo desta aula ainda não subiu',
  /** Explicação dentro do bloco. */
  description:
    'O conteúdo escrito abaixo já vale: leia e marque como concluída. Quando o vídeo for ' +
    'gravado ele aparece aqui, nesta mesma aula, sem você perder o que já concluiu.',
} as const;

/** O botão de concluir muda de rótulo conforme haja ou não vídeo — não se assiste o que não existe. */
export function completeLabel(hasVideo: boolean): string {
  return hasVideo ? 'Marcar como assistida' : 'Marcar como concluída';
}

/** Confirmação depois do clique. */
export function completeToast(hasVideo: boolean): string {
  return hasVideo ? 'Aula marcada como assistida.' : 'Aula marcada como concluída.';
}

/** Já estava concluída: o servidor é idempotente, e a tela não finge que algo mudou. */
export const JA_CONCLUIDA_TOAST = 'Esta aula já estava concluída — nada mudou.';

export const CONCLUIDA_BADGE = 'Concluída';

/**
 * "3 de 12 aulas".
 *
 * Sempre com o total escrito por extenso, nunca só a porcentagem: "25%" não responde
 * "quanto falta", que é a pergunta de quem está no meio do curso.
 */
export function progressLabel(completed: number, total: number): string {
  if (total === 0) return 'Nenhuma aula publicada ainda';
  return `${completed} de ${total} ${total === 1 ? 'aula' : 'aulas'}`;
}

/**
 * A frase que explica por que o denominador é o que é.
 *
 * Aula sem vídeo **conta** no total e **pode** ser concluída — é o que faz o número chegar
 * a 12 de verdade. Tirá-la do total faria o "12" mudar sozinho no dia em que alguém subir
 * um vídeo, e o progresso da pessoa andaria para trás sem ela ter feito nada. Deixá-la no
 * total sem poder concluir seria pendência eterna. Esta linha existe para que a escolha
 * esteja na TELA, e não só no comentário de um arquivo que o usuário nunca abre.
 *
 * `null` quando não há aula sem vídeo: frase que não acrescenta nada é ruído.
 */
export function semVideoHint(lessonsWithoutVideo: number): string | null {
  if (lessonsWithoutVideo <= 0) return null;
  const plural = lessonsWithoutVideo === 1 ? 'aula ainda está' : 'aulas ainda estão';
  return (
    `${lessonsWithoutVideo} ${plural} sem vídeo. Elas contam no total e podem ser ` +
    'concluídas pelo texto — o número não trava esperando a gravação.'
  );
}

/** Porcentagem inteira para a barra. `0` quando não há aula: barra vazia, não NaN. */
export function progressPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((completed / total) * 100));
}

/**
 * Duração em português: `"8 min"`, `"1 h 05"`, `"45 s"`.
 *
 * `null` quando a aula não declara duração — e quem chama simplesmente não mostra nada.
 * Escrever "0 min" ali seria afirmar que a aula dura zero, que é diferente de não saber
 * quanto ela dura (invariante 8).
 */
export function formatDuration(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  if (seconds < 60) return `${seconds} s`;

  const minutos = Math.round(seconds / 60);
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return `${horas} h ${String(resto).padStart(2, '0')}`;
}

/** Estado de uma aula na lista do curso. */
export type LessonState = 'concluida' | 'em-andamento' | 'nao-comecada';

/**
 * **`completedAt` manda; a posição NÃO conclui nada.**
 *
 * A ordem dos testes aqui é o produto inteiro da Fase 11: quem arrastou o player até o fim
 * e não clicou fica em "em andamento", não em "concluída". O schema separa as duas colunas
 * justamente para isso, e deduzir uma da outra aqui jogaria a separação fora na última
 * camada.
 */
export function lessonState(lesson: {
  completedAt: string | Date | null;
  lastPositionSeconds: number;
}): LessonState {
  if (lesson.completedAt !== null) return 'concluida';
  if (lesson.lastPositionSeconds > 0) return 'em-andamento';
  return 'nao-comecada';
}

export const LESSON_STATE_LABEL: Record<LessonState, string> = {
  concluida: 'Concluída',
  'em-andamento': 'Começou',
  'nao-comecada': 'Não começou',
};

/** Caminho da tela de um curso, relativo a `/w/:slug`. */
export function coursePath(workspaceSlug: string, courseSlug: string): string {
  return `/w/${workspaceSlug}/treinamentos/${courseSlug}`;
}

/** Caminho da tela de uma aula. */
export function lessonPath(workspaceSlug: string, courseSlug: string, lessonSlug: string): string {
  return `/w/${workspaceSlug}/treinamentos/${courseSlug}/${lessonSlug}`;
}
